import React, { useState } from 'react';
import { Search, AlertCircle, CheckCircle, Loader2, Trash2, Edit2, XCircle, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEmailScanTab = () => {
  const [results, setResults] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/email-scan`, { withCredentials: true });
      setResults(res.data);
    } catch { alert('Scan failed'); }
    setScanning(false);
  };

  const handleAction = async (item, action, newEmail = '') => {
    setActionLoading(item.id + action);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/email-scan/${item.collection}/${item.id}`, {
        action,
        new_email: newEmail,
        field: item.field,
      }, { withCredentials: true });
      // Remove from list
      setResults(prev => ({
        ...prev,
        flagged: prev.flagged.filter(f => !(f.id === item.id && f.field === item.field)),
        total_flagged: prev.total_flagged - 1,
      }));
      setEditingId(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    }
    setActionLoading(null);
  };

  return (
    <div className="space-y-4" data-testid="email-scan-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Email Validation Scan</h2>
          <p className="text-xs text-[#4B5563]">Scan all records for invalid, suspicious, or malformed email addresses.</p>
        </div>
        <button onClick={runScan} disabled={scanning}
          className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
          data-testid="run-email-scan-btn">
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {scanning ? 'Scanning...' : 'Run Scan'}
        </button>
      </div>

      {results && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
              <Mail className="w-5 h-5 text-[#BA1826] mx-auto mb-1" />
              <div className="font-heading text-xl font-bold">{results.total_scanned}</div>
              <p className="text-[10px] text-[#4B5563]">Records Scanned</p>
            </div>
            <div className={`border rounded-lg p-4 text-center ${results.total_flagged > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              {results.total_flagged > 0 ? <AlertCircle className="w-5 h-5 text-red-500 mx-auto mb-1" /> : <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />}
              <div className="font-heading text-xl font-bold">{results.total_flagged}</div>
              <p className="text-[10px] text-[#4B5563]">{results.total_flagged > 0 ? 'Invalid Emails Found' : 'All Emails Valid'}</p>
            </div>
          </div>

          {/* Per-Collection Breakdown */}
          {results.by_collection && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
              <p className="text-xs font-semibold text-[#4B5563] mb-2">Breakdown by Collection</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(results.by_collection).map(([coll, data]) => {
                  const label = { event_attendees: 'Attendees', payment_transactions: 'Donations', contact_submissions: 'Contacts', newsletter_subscribers: 'Subscribers', volunteers: 'Volunteers', shop_orders: 'Shop Orders' }[coll] || coll;
                  return (
                    <div key={coll} className={`text-center px-3 py-2 rounded-lg border ${data.flagged > 0 ? 'bg-red-50 border-red-200' : 'bg-[#F8F9FA] border-[#E5E7EB]'}`}>
                      <p className="text-xs font-semibold">{label}</p>
                      <p className="text-[10px] text-[#4B5563]">{data.scanned} scanned{data.flagged > 0 ? ` — ${data.flagged} flagged` : ''}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Flagged List */}
          {results.flagged.length > 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
              <div className="bg-red-50 border-b border-red-200 px-4 py-2">
                <p className="text-xs font-semibold text-red-700">Flagged Emails — Review Required</p>
              </div>
              <div className="divide-y divide-[#F1F5F9]">
                {results.flagged.map((item, idx) => (
                  <div key={`${item.id}-${item.field}-${idx}`} className="px-4 py-3" data-testid={`flagged-email-${item.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-[#0A0A0A]">{item.name || 'Unknown'}</span>
                          <span className="text-[9px] bg-[#F1F5F9] text-[#4B5563] px-1.5 py-0.5 rounded">{item.collection}</span>
                          <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{item.field}</span>
                        </div>
                        <p className="text-sm font-mono text-red-600 mt-0.5 truncate" title={item.email}>{item.email}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.issues.map((issue, i) => (
                            <span key={i} className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{issue}</span>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {editingId === `${item.id}-${item.field}` ? (
                          <div className="flex items-center gap-1">
                            <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
                              placeholder="Corrected email"
                              className="px-2 py-1 text-xs border border-[#E5E7EB] rounded w-40 focus:border-[#BA1826] focus:outline-none" />
                            <button onClick={() => handleAction(item, 'correct', editEmail)}
                              disabled={!editEmail || actionLoading === item.id + 'correct'}
                              className="text-xs bg-green-600 text-white px-2 py-1 rounded disabled:opacity-50">
                              Save
                            </button>
                            <button onClick={() => setEditingId(null)} className="text-xs text-gray-500 px-1">Cancel</button>
                          </div>
                        ) : (
                          <>
                            <button onClick={() => { setEditingId(`${item.id}-${item.field}`); setEditEmail(''); }}
                              className="text-blue-500 hover:text-blue-700 p-1" title="Edit / Correct email">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleAction(item, 'flag_invalid')}
                              disabled={actionLoading === item.id + 'flag_invalid'}
                              className="text-amber-500 hover:text-amber-700 p-1" title="Mark as invalid (exclude from emails)">
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => { if (window.confirm(`Delete this record?\n${item.name} — ${item.email}`)) handleAction(item, 'delete'); }}
                              disabled={actionLoading === item.id + 'delete'}
                              className="text-red-400 hover:text-red-600 p-1" title="Delete record">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!results && !scanning && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <Search className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">Click "Run Scan" to check all email addresses across the system.</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Scans: attendees, donations, contacts, subscribers, volunteers, shop orders</p>
        </div>
      )}
    </div>
  );
};

export default AdminEmailScanTab;
